import { Component, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';

import { Ga4AnalyticsService } from '../../../core/analytics/ga4-analytics.service';
import { AuthStateService } from '../../../core/auth/auth-state.service';
import { NumeronCollectionModel, NumeronItemModel } from '../models/numeron.model';
import { NumeronsService } from '../services/numerons.service';

@Component({
  selector: 'app-delete-collection-numeron-page',
  imports: [RouterLink],
  templateUrl: './delete-collection-numeron-page.component.html',
})
export class DeleteCollectionNumeronPageComponent {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly title = inject(Title);
  private readonly ga4AnalyticsService = inject(Ga4AnalyticsService);
  private readonly numeronsService = inject(NumeronsService);
  private readonly authStateService = inject(AuthStateService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly collectionId = this.route.snapshot.paramMap.get('collectionId') ?? '';
  protected readonly numeronId = this.route.snapshot.paramMap.get('numeronId') ?? '';
  protected readonly cancelHref = this.resolveCancelHref();
  protected readonly isLoading = signal(true);
  protected readonly isDeleting = signal(false);
  protected readonly loadErrorMessage = signal('');
  protected readonly deleteErrorMessage = signal('');
  protected readonly loadedCollection = signal<NumeronCollectionModel | null>(null);
  protected readonly targetNumeron = signal<NumeronItemModel | null>(null);

  public constructor() {
    this.title.setTitle('Delete Numeron');
    this.ga4AnalyticsService.trackPageView({
      contentId: this.numeronId || this.collectionId || undefined,
      contentType: 'Delete Numeron in Collection',
      contentGroup: 'My Numerons',
    });
    this.loadCollection();
  }

  protected canDelete(): boolean {
    return (
      !this.isLoading() &&
      this.loadErrorMessage() === '' &&
      this.loadedCollection() !== null &&
      this.targetNumeron() !== null
    );
  }

  protected numeronTitle(): string {
    return this.targetNumeron()?.title?.trim() || 'this Numeron';
  }

  protected deleteNumeron(): void {
    if (!this.canDelete() || this.isDeleting()) {
      return;
    }

    this.isDeleting.set(true);
    this.deleteErrorMessage.set('');

    this.numeronsService
      .deleteCollectionNumeron(this.collectionId, this.numeronId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: async () => {
          this.isDeleting.set(false);
          await this.router.navigateByUrl(this.cancelHref);
        },
        error: () => {
          this.isDeleting.set(false);
          this.deleteErrorMessage.set('Oops, could not delete Numeron!');
        },
      });
  }

  private resolveCancelHref(): string {
    return this.collectionId ? `/mynumerons/collection/${this.collectionId}` : '/mynumerons';
  }

  private loadCollection(): void {
    if (!this.collectionId || !this.numeronId) {
      this.loadErrorMessage.set('Numeron details are unavailable right now. Please try again.');
      this.isLoading.set(false);
      return;
    }

    this.numeronsService
      .getMyNumeronCollection(this.collectionId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (collection) => {
          const numeron = collection.numerons.find((item) => item._id === this.numeronId) ?? null;
          if (!numeron) {
            this.loadErrorMessage.set(
              'Numeron details are unavailable right now. Please try again.',
            );
            this.isLoading.set(false);
            return;
          }

          if (!this.isPublisherMatch(numeron)) {
            this.loadErrorMessage.set('You can delete only Numerons published by your account.');
            this.isLoading.set(false);
            return;
          }

          this.loadedCollection.set(collection);
          this.targetNumeron.set(numeron);
          this.isLoading.set(false);
        },
        error: () => {
          this.loadErrorMessage.set('Numeron details are unavailable right now. Please try again.');
          this.isLoading.set(false);
        },
      });
  }

  private isPublisherMatch(numeron: NumeronItemModel): boolean {
    const authenticatedPublisherId = this.authStateService.authUser()?.backendUserId?.trim() ?? '';
    const authenticatedDisplayName = this.authStateService.authUser()?.displayName?.trim() ?? '';
    const numeronPublisherId = numeron.user?._id?.trim() ?? '';
    const numeronPublisherName = numeron.user?.displayName?.trim() ?? '';

    if (authenticatedPublisherId) {
      return authenticatedPublisherId === numeronPublisherId;
    }

    if (authenticatedDisplayName) {
      return authenticatedDisplayName === numeronPublisherName;
    }

    return true;
  }
}
